import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { not } from 'ember-truth-helpers';
import GlobalFlowIcon from 'garaje/components/global-flow-icon';
import DropdownMenu from 'garaje/components/ui/classic/dropdown-menu';
import dateFormat from 'garaje/helpers/date-format';
import type Flow from 'garaje/models/flow';
import type GlobalFlow from 'garaje/models/global-flow';
import type Questionnaire from 'garaje/models/questionnaire';

const NUMBER_OF_GROUPS_TO_DISPLAY = 3; // max number of groups to display in the table; if more exist, a "see more" link will appear and open a modal to list all

interface VisitorsQuestionnairesListTableRowSignature {
  Args: {
    canDeleteQuestionnaires: boolean;
    canUpdateQuestionnaires: boolean;
    deleteQuestionnaire: (questionnaire: Questionnaire) => void;
    questionnaire: Questionnaire;
    showRoute: string;
  };
  Element: HTMLTableRowElement;
}

type AnyFlow = Flow | GlobalFlow;
type FlowGroup = { groupName: string; flows: Array<Flow | GlobalFlow> };

function flowNameSort(a: Flow | GlobalFlow, b: Flow | GlobalFlow): number {
  return a.name.localeCompare(b.name);
}

export default class VisitorsQuestionnairesListTableRow extends Component<VisitorsQuestionnairesListTableRowSignature> {
  get flowsOrGroups(): AnyFlow[] | FlowGroup[] {
    const questionnaire = this.args.questionnaire;
    const flows = this.args.questionnaire.flowQuestionnaires.map((fq) => fq.flow);
    if (!questionnaire.isGlobal) {
      // If this questionnaire is a location-level one, it will only be associated with flows at that location.
      // So, we can just show all the flows without needing to group them by location.
      return flows.toSorted(flowNameSort);
    }

    // Otherwise, this is a global questionnaire. It may be associated with global flows, as well as location flows.
    // Global flows should be grouped under a "Global" label that appears first; location flows should be grouped by location name.

    const globalFlows = flows.filter((flow) => flow.isGlobal);
    const locationFlows = flows.filter((flow) => !flow.isGlobal);

    const flowsByLocationName = locationFlows.reduce<Record<string, Flow[]>>((state, flow) => {
      const locationName = flow.location.content!.name;
      if (state[locationName]) {
        state[locationName].push(flow);
      } else {
        state[locationName] = [flow];
      }
      return state;
    }, {});

    const locationGroups = Object.entries(flowsByLocationName)
      .map(([locationName, thisLocationFlows]) => ({
        groupName: locationName,
        flows: thisLocationFlows.toSorted(flowNameSort),
      }))
      .toSorted((a, b) => a.groupName.localeCompare(b.groupName));

    const groups: FlowGroup[] = [];
    if (globalFlows.length > 0) {
      groups.push({ groupName: 'Global', flows: globalFlows.toSorted(flowNameSort) });
    }
    if (locationGroups.length > 0) {
      groups.push(...locationGroups);
    }
    return groups;
  }

  get numberOfQuestions(): number {
    return this.args.questionnaire.questionnaireSections.reduce(
      (total, section) => total + section.questionnaireFields.length,
      0,
    );
  }

  <template>
    <tr data-test-questionnaire-row={{@questionnaire.id}}>
      <td class="align-middle border-b text-sm font-bold py-4" data-test-questionnaire-title>
        <LinkTo @route={{@showRoute}} @model={{@questionnaire.id}} class="text-carbon-70">
          {{@questionnaire.title}}
        </LinkTo>
      </td>
      <td
        class="align-middle border-b text-sm text-carbon-50"
        data-test-questionnaire-question-count
      >{{this.numberOfQuestions}}</td>
      <td class="align-middle border-b text-sm text-carbon-50" data-test-questionnaire-flows>
        <QuestionnaireFlows @flowsOrGroups={{this.flowsOrGroups}} />
      </td>
      <td class="align-middle border-b text-sm" data-test-questionnaire-last-updated>
        {{dateFormat @questionnaire.updatedAt "PP 'at' p"}}
      </td>
      <td class="align-middle border-b" data-test-questionnaire-actions>
        <DropdownMenu @left="-36" @top="5">
          <:trigger>
            <div class="mx-2 font-bold text-xl cursor-pointer">···</div>
          </:trigger>
          <:content as |itemClass|>
            <LinkTo @route={{@showRoute}} @model={{@questionnaire.id}} class={{itemClass}} data-test-view-questionnaire>
              {{if @canUpdateQuestionnaires "Edit" "View"}}
            </LinkTo>
            <button
              type="button"
              class={{itemClass}}
              disabled={{not @canDeleteQuestionnaires}}
              {{on "click" (fn @deleteQuestionnaire @questionnaire)}}
              data-test-delete-questionnaire
            >
              Delete
            </button>
          </:content>
        </DropdownMenu>
      </td>
    </tr>
  </template>
}

interface QuestionnaireFlowsSignature {
  Args: {
    flowsOrGroups: AnyFlow[] | FlowGroup[];
  };
}

function isGroup(flowOrGroup: AnyFlow | FlowGroup): flowOrGroup is FlowGroup {
  return Object.hasOwn(flowOrGroup, 'groupName');
}

class QuestionnaireFlows extends Component<QuestionnaireFlowsSignature> {
  @tracked showingAllGroups = false;

  get hasOnlyFlows(): boolean {
    return this.args.flowsOrGroups.every((flowOrGroup) => !isGroup(flowOrGroup));
  }

  get flows(): AnyFlow[] {
    return <AnyFlow[]>this.args.flowsOrGroups.filter((flowOrGroup) => !isGroup(flowOrGroup));
  }

  get groups(): FlowGroup[] {
    return this.args.flowsOrGroups.filter((flowOrGroup) => isGroup(flowOrGroup));
  }

  get groupsToDisplay(): FlowGroup[] {
    if (this.showingAllGroups) return this.groups;

    return this.groups.slice(0, this.numberOfGroupsToDisplay);
  }

  get hasMoreGroups(): boolean {
    return !!this.numberOfGroupsToDisplay && this.groups.length > this.numberOfGroupsToDisplay;
  }

  get numberOfGroupsToDisplay(): number {
    if (this.showingAllGroups) return Infinity;

    // If we have more groups than NUMBER_OF_GROUPS_TO_DISPLAY + 1, show NUMBER_OF_GROUPS_TO_DISPLAY.
    // The plus-one is to avoid showing "1 more location", which is pointless
    if (this.groups.length > NUMBER_OF_GROUPS_TO_DISPLAY + 1) {
      return NUMBER_OF_GROUPS_TO_DISPLAY;
    }

    return this.groups.length;
  }

  get numberOfGroupsNotShown(): number {
    if (!this.numberOfGroupsToDisplay) return 0;
    return Math.max(this.groups.length - this.numberOfGroupsToDisplay, 0);
  }

  @action
  showAllGroups(): void {
    this.showingAllGroups = true;
  }

  <template>
    {{#if @flowsOrGroups}}
      {{#if this.flows}}
        <QuestionnaireFlowsList @flows={{this.flows}} />
      {{/if}}
      {{#if this.groups}}
        <ul>
          {{#each this.groupsToDisplay as |group|}}
            <li data-test-questionnaire-flows-group={{group.groupName}}><QuestionnaireFlowsGroup
                @group={{group}}
              /></li>
          {{/each}}
          {{#if this.hasMoreGroups}}
            <li>
              <button
                type="button"
                class="btn btn--link !text-gem-50"
                data-test-questionnaire-flows-see-more
                {{on "click" this.showAllGroups}}
              >
                {{this.numberOfGroupsNotShown}}
                more locations&hellip;
              </button>
            </li>
          {{/if}}
        </ul>
      {{/if}}
    {{else}}
      <span class="italic">None</span>
    {{/if}}
  </template>
}

interface QuestionnaireFlowsGroupSignature {
  Args: {
    group: FlowGroup;
  };
}

const QuestionnaireFlowsGroup = <template>
  {{@group.groupName}}
  •
  <QuestionnaireFlowsList @flows={{@group.flows}} />
</template> satisfies TOC<QuestionnaireFlowsGroupSignature>;

interface QuestionnaireFlowsListSignature {
  Args: {
    flows: AnyFlow[];
  };
  Element: HTMLUListElement;
}

const QuestionnaireFlowsList = <template>
  <ul class="inline comma-separated" ...attributes>
    {{#each @flows as |flow|}}
      <li class="inline has-trailing-whitespace">
        <GlobalFlowIcon @show={{flow.isGlobal}}>{{flow.name}}</GlobalFlowIcon>
      </li>
    {{/each}}
  </ul>
</template> satisfies TOC<QuestionnaireFlowsListSignature>;
